import { Heading } from "@/components/ui/heading";
import {
  ChevronDownIcon,
  CloseIcon,
  CopyIcon,
  Icon,
} from "@/components/ui/icon";
import { Text } from "@/components/ui/text";
import {
  Modal,
  ModalBackdrop,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from "@/components/ui/modal";
import { InterfaceModalProps } from "@gluestack-ui/modal/lib/types";
import {
  getOrganizationParticipantsControllerFindAllQueryKey,
  getOrganizationTagsControllerGetTagsQueryKey,
  getUserPartControllerMyParticipantsQueryKey,
  getOrganizationControllerFindOneQueryKey,
  useOrganizationControllerFindOne,
  useOrganizationLinksControllerSendInvitations,
  useOrganizationParticipantsControllerDelete,
  useOrganizationParticipantsControllerUpdate,
  useOrganizationTagsControllerGetTags,
  useInvitationCodeControllerRegenerateInvitationCode,
} from "@/generated/api/api";
import { OrganizationDTO } from "@/generated/api/models";
import { VStack } from "@/components/ui/vstack";
import { Pressable } from "@/components/ui/pressable";
import * as Clipboard from "expo-clipboard";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useToast } from "@/utils/useToast";
import { HStack } from "@/components/ui/hstack";
import { Divider } from "@/components/ui/divider";
import {
  AutoComplete,
  AutoCompleteInput,
  AutoCompleteInputField,
  AutoCompleteInputItem,
  AutoCompleteMenu,
  AutoCompleteMenuItem,
  AutoCompleteMenuItemType,
} from "@/screens/common/auto.complete";
import { z } from "zod";
import {
  Button,
  ButtonIcon,
  ButtonSpinner,
  ButtonText,
} from "@/components/ui/button";
import {
  AlertTriangle,
  Copy,
  Download,
  RefreshCw,
  Send,
  Share,
  TriangleAlert,
} from "lucide-react-native";
import { ParticipantDTO } from "@/generated/api/models";
import { InterfaceAlertDialogProps } from "@gluestack-ui/alert-dialog/lib/types";
import { useQueryClient } from "@tanstack/react-query";
import {
  AlertDialog,
  AlertDialogBackdrop,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
} from "@/components/ui/alert-dialog";
import { useAccessTokenClaims } from "@/utils/session.ctx";
import { getName } from "@/utils/name";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Platform } from "react-native";
import {
  FormControl,
  FormControlError,
  FormControlErrorIcon,
  FormControlErrorText,
  FormControlHelper,
  FormControlHelperText,
  FormControlLabel,
  FormControlLabelText,
} from "@/components/ui/form-control";
import {
  Select,
  SelectBackdrop,
  SelectContent,
  SelectDragIndicator,
  SelectDragIndicatorWrapper,
  SelectIcon,
  SelectInput,
  SelectItem,
  SelectPortal,
  SelectTrigger,
} from "@/components/ui/select";
import QRCodeStyled from "react-native-qrcode-styled";
import { useQRCodeDownloader } from "@/utils/useQRCode";
import { Alert, AlertIcon, AlertText } from "@/components/ui/alert";

const emailValidator = z.string().email();

type AutoCompleteEmailItem = AutoCompleteMenuItemType & {
  isValid: boolean;
};

function EmailAutoComplete({
  emails,
  setEmails,
}: {
  emails: AutoCompleteEmailItem[];
  setEmails: (emails: AutoCompleteEmailItem[]) => void;
}) {
  return (
    <AutoComplete
      onCreate={(val) => {
        if (val.length > 0) {
          return { id: val, isValid: emailValidator.safeParse(val).success };
        }
      }}
      value={emails}
      onChange={setEmails}
    >
      <AutoCompleteInput<AutoCompleteEmailItem>
        renderItem={(value, props) => (
          <AutoCompleteInputItem
            text={value.item.id}
            action={value.item.isValid ? "info" : "error"}
            {...props}
          />
        )}
      >
        <AutoCompleteInputField placeholder="Add people emails here" />
      </AutoCompleteInput>
      <AutoCompleteMenu
        createItem={(value, props) => (
          <AutoCompleteMenuItem item={value} {...props} />
        )}
        renderItem={(info, props) => (
          <AutoCompleteMenuItem text={info.item.id} {...props} />
        )}
      ></AutoCompleteMenu>
    </AutoComplete>
  );
}

export function RestaurantTeamInvitationModal(
  props: { organization: OrganizationDTO } & Pick<
    InterfaceModalProps,
    "isOpen" | "onClose"
  >,
) {
  const queryClient = useQueryClient();

  const { mutate: regenerateCode, isPending: isRegenerating } =
    useInvitationCodeControllerRegenerateInvitationCode();

  const { mutate: sendInvitations, isPending: isSendingInvitations } =
    useOrganizationLinksControllerSendInvitations();

  const [emails, setEmails] = useState<AutoCompleteEmailItem[]>([]);
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);

  const { showToast } = useToast();

  const invitationCode = props.organization.invitationCode;

  const onCopyCode = useCallback(() => {
    if (invitationCode) {
      Clipboard.setStringAsync(invitationCode);
      showToast("success", "Code copied!");
    }
  }, [invitationCode]);

  const validEmails = useMemo(
    () => emails.filter((e) => e.isValid).map((e) => e.id),
    [emails.length],
  );

  return (
    <>
      <Modal avoidKeyboard isOpen={props.isOpen} onClose={props.onClose}>
        <ModalBackdrop />
        <ModalContent className="max-w-[395px]">
          <ModalHeader className="gap-2 items-start">
            <VStack className="gap-1">
              <Heading>Invite your team</Heading>
            </VStack>
            <ModalCloseButton>
              <Icon as={CloseIcon} className="stroke-background-500" />
            </ModalCloseButton>
          </ModalHeader>
          <ModalBody className="mb-0">
            <VStack className="gap-3">
              <VStack className="gap-2">
                <Text size="sm" className="text-typography-500">
                  Share this code with your teammates
                </Text>
                <HStack className="justify-between items-stretch">
                  <Pressable
                    onPress={onCopyCode}
                    className="bg-background-100 rounded-md px-6 justify-center"
                  >
                    <Text className="text-3xl font-bold tracking-[0.3em] text-primary-500">
                      {invitationCode ?? "------"}
                    </Text>
                  </Pressable>
                  <VStack className="gap-2">
                    <Button variant="outline" size="sm" onPress={onCopyCode}>
                      <ButtonIcon as={Copy} />
                      <ButtonText>Copy</ButtonText>
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onPress={() => setResetConfirmOpen(true)}
                    >
                      <ButtonIcon as={RefreshCw} />
                      <ButtonText>Reset</ButtonText>
                    </Button>
                  </VStack>
                </HStack>
              </VStack>
              <Divider />
              <VStack className="gap-3">
                <Text size="sm" className="text-typography-500">
                  Or send via email
                </Text>
                <EmailAutoComplete emails={emails} setEmails={setEmails} />
                {validEmails.length > 0 && (
                  <Button
                    isDisabled={isSendingInvitations}
                    onPress={() =>
                      sendInvitations(
                        {
                          organizationId: props.organization.id,
                          data: {
                            emails: validEmails,
                            isPublic: true,
                          },
                        },
                        {
                          onSuccess: () => {
                            showToast(
                              "success",
                              "Invitations have been successfully sent!",
                            );
                            setEmails([]);
                          },
                        },
                      )
                    }
                  >
                    <ButtonIcon as={Send} />
                    <ButtonText>Send invitations</ButtonText>
                    {isSendingInvitations && <ButtonSpinner />}
                  </Button>
                )}
              </VStack>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
      <AlertDialog
        isOpen={resetConfirmOpen}
        onClose={() => setResetConfirmOpen(false)}
      >
        <AlertDialogBackdrop />
        <AlertDialogContent>
          <AlertDialogHeader>
            <Heading className="text-typography-950 font-semibold" size="md">
              Reset invitation code?
            </Heading>
          </AlertDialogHeader>
          <AlertDialogBody className="mt-3 mb-4">
            <Text size="sm">
              Anyone with the current code will no longer be able to join your
              team.
            </Text>
          </AlertDialogBody>
          <AlertDialogFooter>
            <Button
              variant="outline"
              action="secondary"
              onPress={() => setResetConfirmOpen(false)}
              size="sm"
            >
              <ButtonText>Cancel</ButtonText>
            </Button>
            <Button
              size="sm"
              isDisabled={isRegenerating}
              onPress={() =>
                regenerateCode(
                  { organizationId: props.organization.id },
                  {
                    onSuccess: (data) => {
                      queryClient.setQueryData<OrganizationDTO>(
                        getOrganizationControllerFindOneQueryKey(
                          props.organization.id,
                        ),
                        (old) =>
                          old ? { ...old, invitationCode: data.code } : old,
                      );
                      setResetConfirmOpen(false);
                      showToast("success", "Code has been reset");
                    },
                  },
                )
              }
            >
              <ButtonText>Reset</ButtonText>
              {isRegenerating && <ButtonSpinner />}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

export function RestaurantTeamModalDelete({
  organizationId,
  participant,
  onDelete,
  ...rest
}: {
  organizationId: string;
  participant: ParticipantDTO;
  onDelete?: () => void;
} & Pick<InterfaceAlertDialogProps, "isOpen" | "onClose">) {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const claims = useAccessTokenClaims();

  const userId = claims?.externalClaims?.userId;

  const { mutate, isPending } = useOrganizationParticipantsControllerDelete({
    mutation: {
      onSuccess: () => {
        showToast("success", "Teammate has been successfully removed");
        queryClient.invalidateQueries({
          queryKey:
            getOrganizationParticipantsControllerFindAllQueryKey(
              organizationId,
            ),
        });

        if (userId === participant.userId) {
          queryClient.invalidateQueries({
            queryKey: getUserPartControllerMyParticipantsQueryKey(),
          });
        }

        rest.onClose?.();
        onDelete?.();
      },
    },
  });

  return (
    <AlertDialog {...rest}>
      <AlertDialogBackdrop />
      <AlertDialogContent>
        <AlertDialogHeader>
          <Heading className="text-typography-950 font-semibold" size="md">
            {userId === participant.userId
              ? "Are you sure you want to leave this team?"
              : `Are you sure you want to remove ${getName(participant.user)}?`}
          </Heading>
        </AlertDialogHeader>
        <AlertDialogBody className="mt-3 mb-4">
          <Text size="sm">
            {userId === participant.userId
              ? "You will not be able to receive tips anymore in this venue"
              : `${getName(
                  participant.user,
                )} will not be able to receive tips anymore in this venue`}
          </Text>
        </AlertDialogBody>
        <AlertDialogFooter className="">
          <Button
            variant="outline"
            action="secondary"
            onPress={rest.onClose}
            size="sm"
          >
            <ButtonText>Cancel</ButtonText>
          </Button>
          <Button
            size="sm"
            isDisabled={isPending}
            onPress={() =>
              mutate({ organizationId, participantId: participant.id })
            }
          >
            <ButtonText>
              {userId === participant.userId ? "Leave" : "Delete"}
            </ButtonText>
          </Button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

const ParticipantSchema = z.object({
  role: z.union([z.literal("OWNER"), z.literal("ADMIN"), z.literal("USER")]),
  privateTags: z.string().array(),
  publicTags: z.string().array(),
});

type ParticipantSchemaType = z.infer<typeof ParticipantSchema>;

export function RestaurantTeamModalEdit(
  props: { organizationId: string; participant: ParticipantDTO } & Pick<
    InterfaceModalProps,
    "isOpen" | "onClose"
  >,
) {
  const defaultValues = useMemo(
    () => ({
      role: props.participant.role,
      privateTags: props.participant.privateTags,
      publicTags: props.participant.publicTags,
    }),
    [
      props.participant.role,
      props.participant.privateTags,
      props.participant.publicTags,
    ],
  );

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ParticipantSchemaType>({
    resolver: zodResolver(ParticipantSchema),
    defaultValues,
  });
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  const onClose = () => {
    props.onClose?.();
  };

  useEffect(() => {
    reset(defaultValues);
  }, [props.participant]);

  const { data: tags } = useOrganizationTagsControllerGetTags(
    props.organizationId,
  );
  const { data: organization } = useOrganizationControllerFindOne(
    props.organizationId,
  );
  const { mutateAsync } = useOrganizationParticipantsControllerUpdate({
    mutation: {
      onSuccess: (data, variables, context) => {
        showToast("success", "Team member has been successfully updated");
        queryClient.invalidateQueries({
          queryKey: getOrganizationTagsControllerGetTagsQueryKey(
            props.organizationId,
          ),
        });
        queryClient.setQueryData(
          getOrganizationParticipantsControllerFindAllQueryKey(
            props.organizationId,
          ),
          (old: ParticipantDTO[] | undefined) => {
            if (!old) {
              return;
            }

            const index = old.findIndex((p) => p.id === data.id);

            if (index === -1) {
              queryClient.invalidateQueries({
                queryKey: getOrganizationParticipantsControllerFindAllQueryKey(
                  props.organizationId,
                ),
              });

              return;
            }

            const copy = [...old];
            copy[index] = data;

            return copy;
          },
        );
        reset({
          role: data.role,
          publicTags: data.publicTags,
          privateTags: data.privateTags,
        });
        onClose();
      },
    },
  });

  const onSubmit = (data: ParticipantSchemaType) => {
    return mutateAsync({
      organizationId: props.organizationId,
      participantId: props.participant.id,
      data,
    }).catch(() => {});
  };

  const publicTagsItems = useMemo(() => {
    const uniqueSet = new Set(tags?.public);
    if (organization?.name) {
      uniqueSet.add(organization.name);
    }

    return [...uniqueSet].map((t) => ({ id: t }));
  }, [tags?.public, organization?.name]);

  const privateTagsItems = useMemo(() => {
    return [...new Set(tags?.private)].map((t) => ({ id: t }));
  }, [tags?.private]);

  return (
    <Modal avoidKeyboard isOpen={props.isOpen} onClose={onClose}>
      <ModalBackdrop />
      <ModalContent>
        <ModalHeader className="flex-col items-start gap-0.5">
          <Heading>{getName(props.participant.user)}</Heading>
        </ModalHeader>
        <ModalBody className="mb-4" contentContainerClassName="gap-4">
          <FormControl isInvalid={!!errors?.role} className="w-full">
            <FormControlLabel>
              <FormControlLabelText>Role</FormControlLabelText>
            </FormControlLabel>
            <Controller
              defaultValue={props.participant.role}
              name="role"
              control={control}
              render={({ field: { onChange, onBlur, value } }) => (
                <Select onValueChange={onChange} selectedValue={value}>
                  <SelectTrigger>
                    <SelectInput
                      placeholder="Select role"
                      className="flex-1"
                      onBlur={onBlur}
                    />
                    <SelectIcon className="mr-3" as={ChevronDownIcon} />
                  </SelectTrigger>
                  <SelectPortal>
                    <SelectBackdrop />
                    <SelectContent>
                      <SelectDragIndicatorWrapper>
                        <SelectDragIndicator />
                      </SelectDragIndicatorWrapper>
                      <SelectItem label="OWNER" value="OWNER" />
                      <SelectItem label="ADMIN" value="ADMIN" />
                      <SelectItem label="USER" value="USER" />
                    </SelectContent>
                  </SelectPortal>
                </Select>
              )}
            />
            <FormControlHelper>
              <FormControlHelperText>
                Roles Owner and Admin are allowed to invite people to team,
                assign tags and update tipping sharing rules
              </FormControlHelperText>
            </FormControlHelper>
            <FormControlError>
              <FormControlErrorIcon as={AlertTriangle} />
              <FormControlErrorText>
                {errors?.role?.message}
              </FormControlErrorText>
            </FormControlError>
          </FormControl>
          <FormControl isInvalid={!!errors?.publicTags} className="w-full">
            <FormControlLabel>
              <FormControlLabelText>Public tags</FormControlLabelText>
            </FormControlLabel>
            <Controller
              defaultValue={props.participant.publicTags}
              name="publicTags"
              control={control}
              render={({ field: { onChange, onBlur, value } }) => (
                <TagsAutoComplete
                  tags={value.map((v) => ({ id: v }))}
                  setTags={(tags) => onChange(tags.map((t) => t.id))}
                  onBlur={onBlur}
                  items={publicTagsItems}
                />
              )}
            />
            <FormControlHelper>
              <FormControlHelperText>
                These tags are publicly visible for tippers on business page. If
                no public tags assigned to the user, user won't be able to
                receive tips.
              </FormControlHelperText>
            </FormControlHelper>
            <FormControlError>
              <FormControlErrorIcon as={AlertTriangle} />
              <FormControlErrorText>
                {errors?.role?.message}
              </FormControlErrorText>
            </FormControlError>
          </FormControl>
          <FormControl isInvalid={!!errors?.privateTags} className="w-full">
            <FormControlLabel>
              <FormControlLabelText>Private tags</FormControlLabelText>
            </FormControlLabel>
            <Controller
              defaultValue={props.participant.publicTags}
              name="privateTags"
              control={control}
              render={({ field: { onChange, onBlur, value } }) => (
                <TagsAutoComplete
                  tags={value.map((v) => ({ id: v }))}
                  setTags={(tags) => onChange(tags.map((t) => t.id))}
                  onBlur={onBlur}
                  items={privateTagsItems}
                />
              )}
            />
            <FormControlHelper>
              <FormControlHelperText>
                These tags are invisible to tippers. It can be used to organize
                and setup additional distribution logic for tips sharing across
                team.
              </FormControlHelperText>
            </FormControlHelper>
            <FormControlError>
              <FormControlErrorIcon as={AlertTriangle} />
              <FormControlErrorText>
                {errors?.role?.message}
              </FormControlErrorText>
            </FormControlError>
          </FormControl>
        </ModalBody>
        <ModalFooter className="flex-col items-start">
          <Button
            className="w-full gap-2"
            onPress={handleSubmit(onSubmit)}
            isDisabled={isSubmitting}
          >
            <ButtonText>Save</ButtonText>
            {isSubmitting && <ButtonSpinner />}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

function TagsAutoComplete({
  tags,
  items,
  setTags,
  onBlur,
}: {
  tags: AutoCompleteMenuItemType[];
  items: AutoCompleteMenuItemType[];
  setTags: (tags: AutoCompleteMenuItemType[]) => void;
  onBlur: () => void;
}) {
  return (
    <AutoComplete
      onCreate={(val) => {
        if (val.length > 0) {
          return { id: val };
        }
      }}
      filter={(items, val) =>
        items.filter((i) => i.id.toLowerCase().includes(val.toLowerCase()))
      }
      value={tags}
      onChange={setTags}
      items={items}
    >
      <AutoCompleteInput
        renderItem={(value, props) => (
          <AutoCompleteInputItem
            text={value.item.id}
            action="info"
            {...props}
          />
        )}
      >
        <AutoCompleteInputField placeholder="Enter tags" onBlur={onBlur} />
      </AutoCompleteInput>
      <AutoCompleteMenu
        createItem={(value, props) => (
          <AutoCompleteMenuItem item={value} {...props} />
        )}
        renderItem={(info, props) => (
          <AutoCompleteMenuItem text={info.item.id} {...props} />
        )}
      ></AutoCompleteMenu>
    </AutoComplete>
  );
}

export function RestaurantTeamModalQRCode({
  participant,
  ...props
}: { organizationId: string; participant: ParticipantDTO } & Pick<
  InterfaceModalProps,
  "isOpen" | "onClose"
>) {
  const { qrCodeRef, onCopy, onShare, link } = useQRCodeDownloader({
    participant,
  });

  return (
    <Modal avoidKeyboard isOpen={props.isOpen} onClose={props.onClose}>
      <ModalBackdrop />
      <ModalContent>
        <ModalHeader>
          <Heading>{getName(participant.user)}</Heading>
          <ModalCloseButton>
            <Icon
              as={CloseIcon}
              size="md"
              className="stroke-background-400 group-[:hover]/modal-close-button:stroke-background-700 group-[:active]/modal-close-button:stroke-background-900 group-[:focus-visible]/modal-close-button:stroke-background-900"
            />
          </ModalCloseButton>
        </ModalHeader>
        <ModalBody contentContainerClassName="items-center">
          <QRCodeStyled
            ref={qrCodeRef}
            data={link}
            padding={20}
            pieceSize={8}
          />
          {participant.publicTags.length === 0 && (
            <Alert action="warning" variant="solid">
              <AlertIcon as={TriangleAlert} className="min-w-5" />
              <AlertText>
                You may use this QR-code for printing, but it won't work until
                at least one public tag is assigned to user.
              </AlertText>
            </Alert>
          )}
        </ModalBody>
        <ModalFooter className="flex-col items-center">
          <HStack className="gap-4">
            <Button onPress={onCopy} variant="outline">
              <ButtonIcon as={Copy} size={20} className="text-typography-950" />
              <ButtonText>Copy link</ButtonText>
            </Button>
            <Button onPress={onShare}>
              <ButtonIcon as={Platform.OS === "web" ? Download : Share} />
              <ButtonText>
                {Platform.OS === "web" ? "Download PNG" : "Share"}
              </ButtonText>
            </Button>
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
